import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  Platform,
  Modal,
  Linking,
  Pressable,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../screens/ThemeContext';
import { useDocumentManager } from '../private/useDocumentManager';
import { collection, query, where, getDocs, doc as firestoreDoc, getDoc } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import { useAuth } from '../private/AuthContext';
import * as FileSystem from 'expo-file-system';
import PurchaseService from '../services/PurchaseService';
import { findMatchingPurchase } from '../services/documentIdentity';

// Adaptations pour le web
const isWeb = typeof window !== 'undefined';
const StatusBar = Platform.OS === 'web' ? () => null : require('react-native').StatusBar;

/* Ouverture de lien unifiée Web/Android */
const openExternal = (url) => {
  if (!url) return;
  if (isWeb) window.open(url, '_blank', 'noopener,noreferrer');
  else Linking.openURL(url);
};

/* Markdown léger (gras, italique, souligné, liens) pour la description */
const isHttpUrl = (str) => /^https?:\/\//i.test(String(str || ''));

const MarkdownInline = ({ text = '', theme, numberOfLines }) => {
  const regex = /(\*\*[^*]+?\*\*|__[^_]+?__|\*[^*]+?\*|https?:\/\/\S+)/g;
  const parts = [];
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    const token = m[0];
    if (token.startsWith('**')) parts.push({ type: 'bold', value: token.slice(2, -2) });
    else if (token.startsWith('__')) parts.push({ type: 'underline', value: token.slice(2, -2) });
    else if (token.startsWith('*')) parts.push({ type: 'italic', value: token.slice(1, -1) });
    else if (isHttpUrl(token)) parts.push({ type: 'link', value: token });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push({ type: 'text', value: text.slice(lastIndex) });

  return (
    <Text numberOfLines={numberOfLines} ellipsizeMode="tail" style={{ fontSize: 14, lineHeight: 22, color: theme.textSecondary }}>
      {parts.map((p, i) => {
        const style = {};
        if (p.type === 'bold') style.fontWeight = '700';
        if (p.type === 'italic') style.fontStyle = 'italic';
        if (p.type === 'underline') style.textDecorationLine = 'underline';
        if (p.type === 'link') {
          return (
            <Text
              key={`md-${i}`}
              style={{ textDecorationLine: 'underline', color: theme.primary }}
              onPress={() => openExternal(p.value)}
            >
              {p.value}
            </Text>
          );
        }
        return (
          <Text key={`md-${i}`} style={style}>
            {p.value}
          </Text>
        );
      })}
    </Text>
  );
};

/* Wrapper hover compatible RN Web (pas de :hover dans StyleSheet) */
const Hoverable = ({ style, hoverStyle, children, onPress }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <Pressable
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      onPress={onPress}
      style={[style, hover ? hoverStyle : null]}
    >
      {children}
    </Pressable>
  );
};

export default function PurchasedDocumentDetailScreen({ route, navigation }) {
  const { document } = route.params;
  const { theme } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [purchasedDocs, setPurchasedDocs] = useState([]);
  const { downloadedDocs } = useDocumentManager();
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [documentsAnglais, setDocumentsAnglais] = useState(document.documentsAnglais || []);

  useEffect(() => {
    checkPurchasedDocuments();
  }, [user, document.id]);

  // Récupérer documentsAnglais depuis Firestore en temps réel (pour les bords)
  useEffect(() => {
    const fetchDocumentsAnglais = async () => {
      if (document.type !== 'bords') return;
      try {
        const docRef = firestoreDoc(db, 'bords', document.id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.documentsAnglais && data.documentsAnglais.length > 0) {
            setDocumentsAnglais(data.documentsAnglais);
          }
        }
      } catch (error) {
        console.error('Erreur fetch documentsAnglais:', error);
      }
    };
    fetchDocumentsAnglais();
  }, [document.id, document.type]);

  // Debug pour inspecter la structure du document
  const debugDocumentStructure = () => {
    console.log('=== DEBUG DOCUMENT STRUCTURE ===');
    console.log('Document type:', document.type);
    console.log('Document.arretes:', document.arretes);
    console.log('Document.arrete:', document.arrete);
    console.log('Document.restrictionTelechargement:', document.restrictionTelechargement);
    console.log('Document.feuilletage:', document.feuilletage);
    console.log('Document.echantillons:', document.echantillons);
    console.log('Full document keys:', Object.keys(document));
    console.log('Full document:', JSON.stringify(document, null, 2));
    console.log('=== END DEBUG ===');
  };

  useEffect(() => {
    debugDocumentStructure();
  }, [document]);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const checkPurchasedDocuments = async () => {
    setLoading(true);
    try {
      if (!user) {
        setPurchasedDocs([]);
        return;
      }

      const purchasesRef = collection(db, 'purchases');
      const q = query(
        purchasesRef,
        where('userId', '==', user.uid),
        where('documentId', '==', document.id),
        where('status', '==', 'completed')
      );

      const querySnapshot = await getDocs(q);
      const purchases = querySnapshot.docs.map(doc => doc.data());
      setPurchasedDocs(purchases);
    } catch (error) {
      console.error('Error checking purchases:', error);
      setPurchasedDocs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Fonction d'alerte compatible web/mobile
  const showAlert = (title, message) => {
    if (isWeb) {
      window.alert(`${title}\n\n${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  // Nouvelle fonction pour afficher l'alerte de restriction sur web
  const showRestrictionAlert = () => {
    showAlert(
      '📱 Application Mobile Requise',
      'Ce document est protégé et ne peut être consulté que sur l\'application mobile CD Connect.\n\nTéléchargez l\'application pour accéder à ce contenu.'
    );
  };

  const handleDownload = async (docItem, groupId = null) => {
    // Vérification de la connexion utilisateur
    if (!user) {
      showAlert('Connexion requise', 'Vous devez être connecté pour acheter ou télécharger ce document.');
      return;
    }

    try {
      // 1. Cas BORDS (type bords)
      if (document.type === 'bords') {
        const isPurchased = purchasedDocs.some(purchase => purchase.documentId === document.id);
        if (!isPurchased && !docItem?.gratuit) {
          navigation.navigate('PaymentScreen', { document, documentDetail: document });
          return;
        }

        // ✅ Vérifier la restriction sur web
        if (isWeb && document.restrictionTelechargement) {
          showRestrictionAlert();
          return;
        }

        // Récupérer l'URL en temps réel depuis Firestore
        let docUrl = await PurchaseService.getDocumentUrl(document.id, 'bords');
        // Fallback sur les données locales
        if (!docUrl) {
          docUrl = document.document || document.url || document.documents?.[0] || null;
        }
        if (!docUrl) {
          showAlert('Erreur', 'URL du document non disponible');
          return;
        }
        openExternal(docUrl);
        return;
      }

      // 2. Cas ARRETE (toujours accessible, pas de restriction)
      if (docItem.isArrete) {
        let docUrl = null;

        if (docItem.document) docUrl = docItem.document;
        else if (docItem.url) docUrl = docItem.url;
        else if (docItem.documentUrl) docUrl = docItem.documentUrl;

        if (!docUrl) {
          showAlert('Erreur', 'Aucun document ou lien disponible pour cet arrêté');
          return;
        }

        openExternal(docUrl);
        return;
      }

      // 3. Cas CONCOURS
      if (document.type === 'concours') {
        // Correspondance tolérante aux modifications admin : une épreuve
        // renommée reste reconnue comme achetée. Sans cela, l'utilisateur
        // qui a déjà payé se voyait proposer de PAYER UNE SECONDE FOIS.
        const isPurchased = !!findMatchingPurchase(purchasedDocs, docItem, {
          documentId: document.id, groupId,
        });

        const isFree = docItem.gratuit === true;

        if (!isPurchased && !isFree) {
          navigation.navigate('PaymentScreen', {
            document,
            documentDetail: {
              ...docItem,
              id: Math.random().toString(),
              groupId
            }
          });
          return;
        }

        // ✅ Vérifier la restriction sur web
        if (isWeb && document.restrictionTelechargement) {
          showRestrictionAlert();
          return;
        }

        // Récupérer l'URL en temps réel depuis Firestore
        let docUrl = await PurchaseService.getDocumentUrl(document.id, 'concours', {
          titre: docItem.titre,
          annee: docItem.annee,
          filiere: docItem.filiere,
          groupId: groupId,
          // Identité stable + auto-réparation (voir documentIdentity.js)
          epreuveId: docItem.epreuveId || docItem.id,
          purchaseId: docItem.purchaseId,
        });
        // Fallback sur les données locales puis les achats
        if (!docUrl) {
          docUrl = docItem.document || docItem.url || null;
        }
        if (!docUrl) {
          const purchase = findMatchingPurchase(purchasedDocs, docItem, {
            documentId: document.id, groupId,
          });
          docUrl = purchase?.url;
        }

        if (!docUrl) {
          console.error('URL manquante pour le document:', {
            docItem,
            groupId,
            isPurchased,
            purchasedDocs
          });
          showAlert('Erreur', 'URL du document non disponible');
          return;
        }

        openExternal(docUrl);
        return;
      }

      // 4. Cas DOCUMENT SIMPLE
      if (document.type === 'documents') {
        // ✅ Vérifier la restriction sur web
        if (isWeb && document.restrictionTelechargement) {
          showRestrictionAlert();
          return;
        }

        // Récupérer l'URL en temps réel depuis Firestore
        let docUrl = await PurchaseService.getDocumentUrl(document.id, 'documents');
        // Fallback sur les données locales
        if (!docUrl) {
          docUrl = document.document || document.url || null;
        }
        if (!docUrl) {
          showAlert('Erreur', 'URL du document non disponible');
          return;
        }
        openExternal(docUrl);
        return;
      }
    } catch (error) {
      console.error('Erreur lors de l\'ouverture du document:', error);
      showAlert('Erreur', 'Impossible d\'ouvrir le document');
    }
  };

  const getImageSource = (doc) => {
    switch (doc.type) {
      case 'bords':
        return doc.image;
      case 'concours':
      case 'documents':
        return doc.coverImage;
      default:
        return null;
    }
  };

  const renderDocumentInfo = () => {
    if (document.type === 'bords') {
      return (
        <View style={[styles.infoCard, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <Text style={[styles.documentTitle, { color: theme.text }]}>
            {document.titre}
          </Text>

          <View style={styles.metaInfo}>
            <View style={[styles.badge, { backgroundColor: theme.primary + '20' }]}>
              <MaterialCommunityIcons
                name="book-open-variant"
                size={16}
                color={theme.primary}
              />
              <Text style={[styles.badgeText, { color: theme.primary }]}>
                Bord
              </Text>
            </View>
            {/* Badge de restriction */}
            {isWeb && document.restrictionTelechargement && (
              <View style={[styles.badge, { backgroundColor: theme.danger + '20', marginLeft: 8 }]}>
                <MaterialCommunityIcons
                  name="cellphone"
                  size={16}
                  color={theme.danger}
                />
                <Text style={[styles.badgeText, { color: theme.danger }]}>
                  App Mobile Requise
                </Text>
              </View>
            )}
          </View>

          <View style={styles.statsContainer}>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="translate" size={24} color={theme.primary} />
              <Text style={[styles.statValue, { color: theme.text }]}>
                {document.langue === 'fr' ? 'Français' : 'Anglais'}
              </Text>
              <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Langue</Text>
            </View>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="file-document" size={24} color={theme.primary} />
              <Text style={[styles.statValue, { color: theme.text }]}>
                {document.nombrePages}
              </Text>
              <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Pages</Text>
            </View>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="currency-usd" size={24} color={theme.primary} />
              {parseFloat(document.reductionPourcent || 0) > 0 ? (
                <View style={{ alignItems: 'center' }}>
                  <Text style={[styles.statValue, { color: theme.danger, textDecorationLine: 'line-through', fontSize: 12 }]}>
                    {document.prixInitial || document.prix} FCFA
                  </Text>
                  <Text style={[styles.statValue, { color: theme.primary, fontWeight: 'bold' }]}>
                    {document.prixFinal || document.prix} FCFA
                  </Text>
                </View>
              ) : (
                <Text style={[styles.statValue, { color: theme.text }]}>
                  {document.prix} FCFA
                </Text>
              )}
              <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Prix</Text>
            </View>
          </View>

          <View style={styles.separator} />

          <Text style={[styles.sectionTitle, { color: theme.text }]}>Description</Text>
          <MarkdownInline text={document.description} theme={theme} />

          {document.echantillons && document.echantillons.length > 0 && (
            <>
              <View style={styles.separator} />
              <Text style={[styles.sectionTitle, { color: theme.text }]}>Échantillons</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.echantillonsRow}>
                  {document.echantillons.map((image, index) => (
                    <TouchableOpacity
                      key={index}
                      style={styles.echantillonContainer}
                      onPress={() => {
                        setSelectedImage(image);
                        setImageModalVisible(true);
                      }}
                    >
                      <Image
                        source={{ uri: image }}
                        style={styles.echantillonImage}
                      />
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </>
          )}

          {document.feuilletage && document.feuilletage !== '' && document.feuilletage !== null && (
            <>
              <View style={styles.separator} />
              <Text style={[styles.sectionTitle, { color: theme.text }]}>Feuilletage</Text>
              <Text style={[styles.description, { color: theme.textSecondary, marginBottom: 12 }]}>
                Parcourez quelques pages avant l'achat
              </Text>
              <Hoverable
                style={[styles.feuilletageButton, { backgroundColor: theme.primary + '20' }]}
                hoverStyle={styles.hoverLift}
                onPress={() => {
                  console.log('Feuilletage URL:', document.feuilletage);
                  if (document.feuilletage) openExternal(document.feuilletage);
                }}
              >
                <MaterialCommunityIcons
                  name="file-pdf-box"
                  size={24}
                  color={theme.primary}
                  style={{ marginRight: 8 }}
                />
                <Text style={[styles.feuilletageButtonText, { color: theme.primary }]}>
                  Voir l'aperçu
                </Text>
              </Hoverable>
            </>
          )}

        </View>
      );
    } else {
      return (
        <View style={[styles.infoCard, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <Text style={[styles.documentTitle, { color: theme.text }]}>
            {document.type === 'concours' ?
              (document.sigle ? `${document.nomComplet} (${document.sigle})` : document.nomComplet) :
              document.titre}
          </Text>

          <View style={styles.metaInfo}>
            <View style={[styles.badge, { backgroundColor: theme.primary + '20' }]}>
              <MaterialCommunityIcons
                name={document.type === 'concours' ? 'school' : 'gavel'}
                size={16}
                color={theme.primary}
              />
              <Text style={[styles.badgeText, { color: theme.primary }]}>
                {document.type === 'concours' ? 'Épreuves' : 'Arrêté'}
              </Text>
            </View>
            {/* Badge de restriction */}
            {isWeb && document.restrictionTelechargement && (
              <View style={[styles.badge, { backgroundColor: theme.danger + '20', marginLeft: 8 }]}>
                <MaterialCommunityIcons
                  name="cellphone"
                  size={16}
                  color={theme.danger}
                />
                <Text style={[styles.badgeText, { color: theme.danger }]}>
                  App Mobile Requise
                </Text>
              </View>
            )}
          </View>

          {document.type === 'concours' && (
            <View style={styles.statsContainer}>
              <View style={styles.statItem}>
                <MaterialCommunityIcons name="school" size={24} color={theme.primary} />
                <Text style={[styles.statValue, { color: theme.text }]}>{document.niveau}</Text>
                <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Niveau</Text>
              </View>
              <View style={styles.statItem}>
                <MaterialCommunityIcons name="translate" size={24} color={theme.primary} />
                <Text style={[styles.statValue, { color: theme.text }]}>
                  {document.langue === 'fr' ? 'Français' : 'Anglais'}
                </Text>
                <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Langue</Text>
              </View>
              <View style={styles.statItem}>
                <MaterialCommunityIcons name="calendar" size={24} color={theme.primary} />
                <Text style={[styles.statValue, { color: theme.text }]}>
                  {document.hasGroups ?
                    (document.groups[0]?.documents[0]?.annee || 'N/A') :
                    (document.documents?.[0]?.annee || 'N/A')}
                </Text>
                <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Année</Text>
              </View>
            </View>
          )}

          <View style={styles.separator} />

          <Text style={[styles.sectionTitle, { color: theme.text }]}>Description</Text>
          <MarkdownInline text={document.description} theme={theme} />
        </View>
      );
    }
  };

  // Fonction renderArretes
  const renderArretes = () => {
    let arretes = [];

    if (document.arretes && Array.isArray(document.arretes) && document.arretes.length > 0) {
      arretes = document.arretes;
    } else if (document.arrete) {
      arretes = [document.arrete];
    }

    if (arretes.length === 0) return null;

    return (
      <View style={[styles.arretesSection, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>
          {arretes.length > 1 ? 'Arrêtés du Concours' : 'Arrêté du Concours'}
        </Text>
        {arretes.map((arrete, index) => (
          <Hoverable
            key={arrete.id || `arrete-${index}`}
            style={[styles.documentItem, { backgroundColor: theme.background, borderColor: theme.border }]}
            hoverStyle={styles.hoverLift}
            onPress={() => handleDownload({ ...arrete, isArrete: true })}
          >
            <View style={styles.documentIcon}>
              <MaterialCommunityIcons
                name="gavel"
                size={24}
                color={theme.primary}
              />
            </View>
            <View style={styles.documentInfo}>
              <Text style={[styles.documentItemTitle, { color: theme.primary }]}>
                {arrete.titre || "Arrêté du concours"}
              </Text>
              {arrete.annee && (
                <Text style={[styles.documentMeta, { color: theme.textSecondary }]}>
                  Année: {arrete.annee}
                </Text>
              )}
              <View style={styles.arreteSourceContainer}>
                {arrete.document && (
                  <View style={[styles.sourceIndicator, { backgroundColor: theme.primary + '15' }]}>
                    <MaterialCommunityIcons name="file-pdf-box" size={16} color={theme.primary} />
                    <Text style={[styles.sourceText, { color: theme.primary }]}>PDF</Text>
                  </View>
                )}
                {arrete.url && (
                  <View style={[styles.sourceIndicator, {
                    backgroundColor: (theme.secondary || theme.primary) + '15',
                    marginLeft: arrete.document ? 8 : 0
                  }]}>
                    <MaterialCommunityIcons name="link" size={16} color={theme.secondary || theme.primary} />
                    <Text style={[styles.sourceText, { color: theme.secondary || theme.primary }]}>Lien</Text>
                  </View>
                )}
              </View>
              <Text style={[styles.documentPrice, { color: theme.primary }]}>
                Gratuit
              </Text>
            </View>
            <View style={[styles.downloadButton, { backgroundColor: theme.primary + '20' }]}>
              <MaterialCommunityIcons
                name="download"
                size={24}
                color={theme.primary}
              />
            </View>
          </Hoverable>
        ))}
      </View>
    );
  };

  const renderGroupedDocuments = () => {
    return document.groups?.map((group) => (
      <View key={group.id} style={styles.groupContainer}>
        <Hoverable
          style={[styles.groupHeader, {
            backgroundColor: theme.surface,
            borderColor: theme.border
          }]}
          hoverStyle={styles.hoverSubtle}
          onPress={() => toggleGroup(group.id)}
        >
          <View style={styles.groupTitleContainer}>
            <MaterialCommunityIcons
              name="account-group"
              size={24}
              color={theme.primary}
            />
            <Text style={[styles.groupTitle, { color: theme.primary }]}>
              {group.nom}
            </Text>
          </View>
          <MaterialCommunityIcons
            name={expandedGroups[group.id] ? "chevron-up" : "chevron-down"}
            size={24}
            color={theme.text}
          />
        </Hoverable>

        {expandedGroups[group.id] && group.documents?.map((doc, index) =>
          renderDocumentItem(doc, index, group.id)
        )}
      </View>
    ));
  };

  const renderDocumentItem = (docItem, index, groupId = null) => {
    // Arrêté (sécurité si appelé ici)
    if (docItem.isArrete) {
      return (
        <Hoverable
          key={index}
          style={[styles.documentItem, { backgroundColor: theme.surface, borderColor: theme.border }]}
          hoverStyle={styles.hoverLift}
          onPress={() => handleDownload(docItem)}
        >
          <View style={styles.documentIcon}>
            <MaterialCommunityIcons
              name="gavel"
              size={24}
              color={theme.primary}
            />
          </View>
          <View style={styles.documentInfo}>
            <Text style={[styles.documentItemTitle, { color: theme.primary }]}>
              {docItem.titre ? docItem.titre : "Arrêté du concours"}
            </Text>
            {docItem.annee && (
              <Text style={[styles.documentMeta, { color: theme.textSecondary }]}>
                Année: {docItem.annee}
              </Text>
            )}
            <Text style={[styles.documentPrice, { color: theme.primary }]}>
              Gratuit
            </Text>
          </View>
          <View style={[styles.downloadButton, { backgroundColor: theme.primary + '20' }]}>
            <MaterialCommunityIcons
              name="download"
              size={24}
              color={theme.primary}
            />
          </View>
        </Hoverable>
      );
    }

    // Épreuves de concours
    if (document.type === 'concours') {
      // Même correspondance tolérante : le cadenas ne doit pas réapparaître
      // sur une épreuve renommée par l'admin.
      const isPurchased = !!findMatchingPurchase(purchasedDocs, docItem, {
        documentId: document.id, groupId,
      });

      const isFree = docItem.gratuit === true;
      const isRestricted = isWeb && document.restrictionTelechargement;

      return (
        <Hoverable
          key={index}
          style={[styles.documentItem, {
            backgroundColor: theme.surface,
            borderColor: theme.border,
            marginLeft: groupId ? 20 : 0
          }]}
          hoverStyle={styles.hoverLift}
          onPress={() => handleDownload(docItem, groupId)}
        >
          <View style={styles.documentIcon}>
            <MaterialCommunityIcons
              name={isRestricted ? "cellphone-lock" : "file-pdf-box"}
              size={24}
              color={isRestricted ? theme.danger : theme.primary}
            />
          </View>
          <View style={styles.documentInfo}>
            <Text style={[styles.documentItemTitle, { color: theme.primary }]}>
              {`${docItem.titre} - ${docItem.annee}`}
            </Text>
            <Text style={[styles.documentMeta, { color: theme.textSecondary }]}>
              {`${docItem.nombrePages} pages • ${docItem.filiere}`}
            </Text>
            {isRestricted && (
              <Text style={[styles.documentMeta, { color: theme.danger, fontWeight: '600', marginTop: 4 }]}>
                📱 Application mobile requise
              </Text>
            )}
            {/* Affichage du prix avec réduction */}
            {isFree ? (
              <Text style={[styles.documentPrice, { color: theme.primary }]}>
                Gratuit
              </Text>
            ) : parseFloat(docItem.reductionPourcent || 0) > 0 ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                <View style={{
                  backgroundColor: theme.danger,
                  paddingHorizontal: 6,
                  paddingVertical: 2,
                  borderRadius: 4,
                  marginRight: 8
                }}>
                  <Text style={{ color: '#fff', fontSize: 10, fontWeight: 'bold' }}>
                    -{docItem.reductionPourcent}%
                  </Text>
                </View>
                <Text style={[styles.documentPrice, {
                  color: theme.textSecondary,
                  textDecorationLine: 'line-through',
                  fontSize: 12,
                  marginRight: 6
                }]}>
                  {docItem.prixInitial || docItem.prix} FCFA
                </Text>
                <Text style={[styles.documentPrice, {
                  color: theme.primary,
                  fontWeight: 'bold'
                }]}>
                  {docItem.prixFinal || docItem.prix} FCFA
                </Text>
              </View>
            ) : (
              <Text style={[styles.documentPrice, { color: theme.primary }]}>
                {docItem.prix} FCFA
              </Text>
            )}
          </View>
          <View style={[styles.downloadButton, {
            backgroundColor: (isPurchased || isFree) ? theme.primary + '20' : theme.danger + '20'
          }]}>
            <MaterialCommunityIcons
              name={(isPurchased || isFree) ? "download" : "lock"}
              size={24}
              color={(isPurchased || isFree) ? theme.primary : theme.danger}
            />
          </View>
        </Hoverable>
      );
    }

    // Document simple
    if (document.type === 'documents') {
      const isRestricted = isWeb && document.restrictionTelechargement;

      return (
        <Hoverable
          key={index}
          style={[styles.documentItem, { backgroundColor: theme.surface, borderColor: theme.border }]}
          hoverStyle={styles.hoverLift}
          onPress={() => handleDownload(document)}
        >
          <View style={styles.documentIcon}>
            <MaterialCommunityIcons
              name={isRestricted ? "cellphone-lock" : "file-pdf-box"}
              size={24}
              color={isRestricted ? theme.danger : theme.primary}
            />
          </View>
          <View style={styles.documentInfo}>
            <Text style={[styles.documentItemTitle, { color: theme.primary }]}>
              {document.titre}
            </Text>
            <Text style={[styles.documentMeta, { color: theme.textSecondary }]}>
              Document PDF
            </Text>
            {isRestricted && (
              <Text style={[styles.documentMeta, { color: theme.danger, fontWeight: '600', marginTop: 4 }]}>
                📱 Application mobile requise
              </Text>
            )}
          </View>
          <View style={[styles.downloadButton, { backgroundColor: theme.primary + '20' }]}>
            <MaterialCommunityIcons
              name="download"
              size={24}
              color={theme.primary}
            />
          </View>
        </Hoverable>
      );
    }

    // Bords (affiché en item)
    const isPurchased = purchasedDocs.some(purchase => purchase.documentId === document.id);
    const isRestricted = isWeb && document.restrictionTelechargement;

    return (
      <Hoverable
        key={index}
        style={[styles.documentItem, { backgroundColor: theme.surface, borderColor: theme.border }]}
        hoverStyle={styles.hoverLift}
        onPress={() => handleDownload(document)}
      >
        <View style={styles.documentIcon}>
          <MaterialCommunityIcons
            name={isRestricted ? "cellphone-lock" : "file-pdf-box"}
            size={24}
            color={isRestricted ? theme.danger : theme.primary}
          />
        </View>
        <View style={styles.documentInfo}>
          <Text style={[styles.documentItemTitle, { color: theme.primary }]}>
            {document.titre}
          </Text>
          <Text style={[styles.documentMeta, { color: theme.textSecondary }]}>
            {`${document.nombrePages} pages`}
          </Text>
          {isRestricted && (
            <Text style={[styles.documentMeta, { color: theme.danger, fontWeight: '600', marginTop: 4 }]}>
              📱 Application mobile requise
            </Text>
          )}
          {parseFloat(document.reductionPourcent || 0) > 0 ? (
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text style={[styles.documentPrice, { color: theme.danger, textDecorationLine: 'line-through', marginRight: 6, fontSize: 12 }]}>
                {document.prixInitial || document.prix} FCFA
              </Text>
              <Text style={[styles.documentPrice, { color: theme.primary, fontWeight: 'bold' }]}>
                {document.prixFinal || document.prix} FCFA
              </Text>
            </View>
          ) : (
            <Text style={[styles.documentPrice, { color: theme.primary }]}>
              {`${document.prix} FCFA`}
            </Text>
          )}
        </View>
        <View style={[styles.downloadButton, {
          backgroundColor: isPurchased ? theme.primary + '20' : theme.danger + '20'
        }]}>
          <MaterialCommunityIcons
            name={isPurchased ? "download" : "lock"}
            size={24}
            color={isPurchased ? theme.primary : theme.danger}
          />
        </View>
      </Hoverable>
    );
  };

  return (
    <View style={[styles.safeArea, { backgroundColor: theme.primary, minHeight: isWeb ? '100vh' : undefined }]}>
      <StatusBar barStyle="light-content" backgroundColor={theme.primary} />
      <ScrollView
        style={[styles.container, { backgroundColor: theme.background }]}
        contentContainerStyle={{ flexGrow: 1, paddingBottom: 40 }}
      >
        <Image
          source={{ uri: getImageSource(document) }}
          style={styles.coverImage}
        />

        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#fff" />
        </TouchableOpacity>

        <View style={styles.content}>
          {renderDocumentInfo()}

          {/* Afficher les arrêtés pour les concours */}
          {document.type === 'concours' && renderArretes()}

          <View style={[styles.documentsSection, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <Text style={[styles.sectionTitle, { color: theme.text }]}>
              {document.type === 'concours' ? 'Épreuves disponibles' : 'Documents disponibles'}
            </Text>
            {loading ? (
              <ActivityIndicator size="large" color={theme.primary} style={styles.loader} />
            ) : (
              <>
                {document.type === 'concours' ? (
                  document.hasGroups ? (
                    renderGroupedDocuments()
                  ) : (
                    document.documents?.map((doc, index) => renderDocumentItem(doc, index))
                  )
                ) : document.type === 'bords' ? (
                  <>
                    {renderDocumentItem(document, 0)}
                    {documentsAnglais && documentsAnglais.length > 0 && (() => {
                      const isPurchased = purchasedDocs.some(purchase => purchase.documentId === document.id);
                      const isRestricted = isWeb && document.restrictionTelechargement;
                      return (
                        <Hoverable
                          style={[styles.documentItem, { backgroundColor: theme.surface, borderColor: theme.border }]}
                          hoverStyle={styles.hoverLift}
                          onPress={async () => {
                            if (isRestricted) {
                              showRestrictionAlert();
                              return;
                            }
                            if (!isPurchased) {
                              showAlert('Achat requis', 'Vous devez acheter ce bord pour accéder à la version anglaise.');
                              return;
                            }
                            let anglaisUrl = await PurchaseService.getDocumentUrlAnglais(document.id);
                            if (!anglaisUrl && documentsAnglais.length > 0) {
                              const firstDoc = documentsAnglais[0];
                              anglaisUrl = typeof firstDoc === 'string' ? firstDoc : firstDoc?.url || firstDoc?.document || null;
                            }
                            if (anglaisUrl) {
                              openExternal(anglaisUrl);
                            } else {
                              showAlert('Erreur', 'URL de la version anglaise non disponible');
                            }
                          }}
                        >
                          <View style={styles.documentIcon}>
                            <MaterialCommunityIcons
                              name={isRestricted ? "cellphone-lock" : "translate"}
                              size={24}
                              color={isRestricted ? theme.danger : '#FF6F00'}
                            />
                          </View>
                          <View style={styles.documentInfo}>
                            <Text style={[styles.documentItemTitle, { color: '#FF6F00' }]}>
                              {document.titre} (English Version)
                            </Text>
                            <Text style={[styles.documentMeta, { color: theme.textSecondary }]}>
                              {`${document.nombrePages} pages • Version anglaise`}
                            </Text>
                            {isRestricted && (
                              <Text style={[styles.documentMeta, { color: theme.danger, fontWeight: '600', marginTop: 4 }]}>
                                Application mobile requise
                              </Text>
                            )}
                          </View>
                          <View style={[styles.downloadButton, {
                            backgroundColor: isPurchased ? '#FF6F00' + '20' : theme.danger + '20'
                          }]}>
                            <MaterialCommunityIcons
                              name={isPurchased ? "download" : "lock"}
                              size={24}
                              color={isPurchased ? '#FF6F00' : theme.danger}
                            />
                          </View>
                        </Hoverable>
                      );
                    })()}
                  </>
                ) : document.type === 'documents' ? (
                  renderDocumentItem(document, 0)
                ) : null}
              </>
            )}
          </View>
        </View>
      </ScrollView>

      {/* Modal pour afficher les échantillons en plein écran */}
      <Modal
        visible={imageModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setImageModalVisible(false)}
      >
        <View style={styles.modalContainer}>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={() => setImageModalVisible(false)}
          >
            <MaterialCommunityIcons name="close" size={24} color="#fff" />
          </TouchableOpacity>
          {selectedImage && (
            <Image
              source={{ uri: selectedImage }}
              style={styles.fullScreenImage}
              resizeMode="contain"
            />
          )}
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    minHeight: isWeb ? '100vh' : undefined,
  },
  container: {
    flex: 1,
    minHeight: isWeb ? '100vh' : undefined,
  },
  coverImage: {
    width: '100%',
    height: 250,
    objectFit: isWeb ? 'cover' : undefined,
  },
  backButton: {
    position: 'absolute',
    top: 20,
    left: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: 10,
    padding: 8,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  content: {
    padding: 20,
    marginTop: -40,
    maxWidth: isWeb ? 1200 : undefined,
    alignSelf: isWeb ? 'center' : undefined,
    width: isWeb ? '100%' : undefined,
  },
  infoCard: {
    padding: 20,
    borderRadius: 20,
    borderWidth: 1,
    marginBottom: 20,
    ...(isWeb ? {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 3,
    } : {}),
  },
  documentTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
    lineHeight: isWeb ? 1.3 : undefined,
  },
  metaInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  badgeText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 20,
    paddingVertical: 20,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
    flexWrap: isWeb ? 'wrap' : undefined,
  },
  statItem: {
    alignItems: 'center',
    minWidth: isWeb ? 120 : undefined,
  },
  statValue: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 8,
    marginBottom: 4,
    textAlign: 'center',
  },
  statLabel: {
    fontSize: 12,
    textAlign: 'center',
  },
  separator: {
    height: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    marginVertical: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  description: {
    fontSize: 14,
    lineHeight: 22,
  },
  documentsSection: {
    padding: 20,
    borderRadius: 20,
    borderWidth: 1,
    ...(isWeb ? {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 3,
    } : {}),
  },
  arretesSection: {
    padding: 20,
    borderRadius: 20,
    borderWidth: 1,
    marginBottom: 20,
    ...(isWeb ? {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 3,
    } : {}),
  },
  documentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  documentIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  documentInfo: {
    flex: 1,
  },
  documentItemTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  documentMeta: {
    fontSize: 14,
    marginBottom: 4,
  },
  documentPrice: {
    fontSize: 14,
    fontWeight: '600',
  },
  downloadButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  loader: {
    padding: 20,
  },
  echantillonsRow: {
    flexDirection: 'row',
    marginTop: 12,
    ...(isWeb ? { overflowX: 'auto', paddingBottom: 10 } : {}),
  },
  echantillonContainer: {
    marginRight: 12,
    borderRadius: 8,
    overflow: 'hidden',
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  echantillonImage: {
    width: 120,
    height: 120,
    borderRadius: 8,
    objectFit: isWeb ? 'cover' : undefined,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    ...(isWeb ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 } : {}),
  },
  fullScreenImage: {
    width: '100%',
    height: '100%',
    objectFit: isWeb ? 'contain' : undefined,
  },
  closeButton: {
    position: 'absolute',
    top: 40,
    right: 20,
    zIndex: 1,
    padding: 10,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  groupContainer: {
    marginBottom: 16,
  },
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderWidth: 1,
    borderRadius: 12,
    marginBottom: 8,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  groupTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  groupTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 12,
  },
  feuilletageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 12,
    marginTop: 8,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  feuilletageButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  arreteSourceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
    marginBottom: 4,
    flexWrap: 'wrap',
  },
  sourceIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 4,
    marginBottom: 4,
  },
  sourceText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  /* Effets hover appliqués via <Hoverable /> */
  hoverLift: isWeb
    ? {
        transform: [{ translateY: -2 }],
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
        elevation: 6,
      }
    : {},
  hoverSubtle: isWeb
    ? { backgroundColor: 'rgba(0,0,0,0.03)' }
    : {},
});
//web